import Combobox from "@/components/Combobox";
import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/axios";
import { getListPeriode } from "@/services/getData";
import { Periode } from "@/types";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { LuDownload } from "react-icons/lu";
import { RiFileExcel2Fill } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type importDataState = {
  file: File | null;
  fileName: string;
  id_sys_periode: string;
};

const importDataSiswaLulus = async (newData: FormData) => {
  const response = await api.post(
    "/import/penerimaan/data-siswa-lulus",
    newData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export default function FormImportDataSiswaLulus({
  closeModal,
}: {
  closeModal: () => void;
}) {
  const [openPeriode, setOpenPeriode] = useState(false);
  const [form, setForm] = useState<importDataState>({
    file: null,
    fileName: "",
    id_sys_periode: "",
  });

  const { data: dataPeriode, isPending: isPendingDataPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(),
  });

  const optionsPeriode =
    dataPeriode?.map((periode: Periode) => ({
      label: `${periode.tahun} - ${periode.jalur_jenis?.nama_jalur_jenis} - Gelombang ${periode.gelombang}`,
      value: `${periode.id_sys_periode}`,
    })) || [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) setForm({ ...form, file: file, fileName: file.name });
  };

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: importDataSiswaLulus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataPendaftaran"] });
      closeModal();
      toast.success(data?.message || "Success.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred.",
      );
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!form.id_sys_periode) {
      toast.error("Pilih periode terlebih dahulu");
      return;
    }

    const formData = new FormData();
    formData.append("id_sys_periode", form.id_sys_periode);
    if (form.file) formData.append("file", form.file);
    mutate(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        title="Import Data Siswa Lulus (data dari pusat)"
        icon={<RiFileExcel2Fill />}
      />

      <div>
        <Label>Periode</Label>
        <Combobox
          isPending={isPendingDataPeriode}
          open={openPeriode}
          setOpen={setOpenPeriode}
          value={form.id_sys_periode}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, id_sys_periode: value }));
          }}
          options={optionsPeriode}
          placeholder="Pilih Periode"
        />
      </div>

      <div>
        <Label>Format Import</Label>
        <Button
          type="button"
          className="w-full flex items-center gap-3"
          variant={"outline"}
          onClick={() => {
            window.open("/format/format-import-data-lulus.xlsx");
          }}
        >
          <LuDownload /> Unduh Format Import Siswa Lulus
        </Button>
      </div>
      <div>
        <Label>File Excel</Label>
        <Input required type="file" onChange={handleFileChange} />
        {form.file && (
          <p className="text-sm text-muted-foreground">
            Selected file: {form.fileName}
          </p>
        )}
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          variant={"outline"}
          type="button"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Import"
          )}
        </Button>
      </div>
    </form>
  );
}
